import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx, useEscape, STOP_PROPAGATION } from './util';

/**
 * Popover — a small floating panel anchored to an element (user menu, •••
 * menus, health details, filter panels). Portal-rendered in its own
 * `.ui-root`. Closes on Escape and on any click outside the panel and anchor.
 *
 * open / onClose · anchorEl (DOM node) · placement: 'bottom-end' (default) |
 * 'bottom-start' | 'top-end' | 'top-start' · pad: 14/16 px padding for
 * text content (menus use the default 6 px) · offset (px, default 6)
 */
export default function Popover({ open, onClose, anchorEl, placement = 'bottom-end', pad = false, offset = 6, role = 'dialog', ariaLabel, className, style, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const close = useCallback(() => { if (onClose) onClose(); }, [onClose]);
  useEscape(open, close);

  const place = useCallback(() => {
    if (!anchorEl || !ref.current) return;
    const a = anchorEl.getBoundingClientRect();
    const p = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const [vert, horiz] = placement.split('-');
    let top = vert === 'top' ? a.top - p.height - offset : a.bottom + offset;
    if (vert !== 'top' && top + p.height > vh - 8 && a.top - p.height - offset > 8) top = a.top - p.height - offset;
    let left = horiz === 'start' ? a.left : a.right - p.width;
    left = Math.max(8, Math.min(left, vw - p.width - 8));
    top = Math.max(8, top);
    setPos({ top, left });
  }, [anchorEl, placement, offset]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains && anchorEl.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [open, place, anchorEl, close]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div className="ui-root" style={{ position: 'static' }}>
      <div
        ref={ref}
        role={role}
        aria-label={ariaLabel}
        className={cx('ui-pop', pad && 'ui-pop--pad', className)}
        style={{ top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? 'visible' : 'hidden', ...style }}
        {...STOP_PROPAGATION}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Menu / MenuItem / MenuSeparator — list content for a Popover.
 * MenuItem: onClick · to (router path; pass `onNavigate`) · danger · disabled · hint (right text) · on (current)
 */
export function Menu({ children, ariaLabel }) {
  return <div className="ui-menu" role="menu" aria-label={ariaLabel}>{children}</div>;
}

export function MenuItem({ children, onClick, danger = false, disabled = false, hint, on = false, as: Tag = 'button', ...rest }) {
  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      role="menuitem"
      className={cx('ui-menu__item', danger && 'ui-menu__item--danger', on && 'is-on')}
      onClick={onClick}
      disabled={Tag === 'button' ? disabled : undefined}
      aria-disabled={disabled || undefined}
      aria-current={on ? 'page' : undefined}
      {...rest}
    >
      <span>{children}</span>
      {hint ? <small>{hint}</small> : null}
    </Tag>
  );
}

export const MenuSeparator = () => <div className="ui-menu__sep" role="separator" />;
export const MenuHead = ({ children }) => <div className="ui-menu__head">{children}</div>;
